"use client";

import Link from "next/link";
import { motion, useReducedMotion } from "@/lib/motion";
import { useUptimeData } from "@/hooks/use-uptime";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { SectionHeader } from "@/components/ui/";
import { smallFeatures } from "./data";
import { BigFeatureCard, DashboardMock, SmallFeatureCard, UptimeMock } from "./parts";

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

export default function WhyProxidizeSection() {
  const prefersReducedMotion = useReducedMotion();
  const { availability: uptime, status: uptimeStatus } = useUptimeData();

  return (
    <section className="py-section border-border border-b" aria-labelledby="why-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Why Proxidize"
            id="why-heading"
            inlineBody
            heading="Built for reliable data."
            body="Clean IPs, simple access, transparent usage, and real support — without the usual proxy complexity."
          />
          <p className="text-muted-foreground mt-4 text-sm leading-relaxed">
            Learn more about Proxidize, a{" "}
            <Link
              href="/about"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              managed cloud proxy provider
            </Link>{" "}
            built for developers, enterprises, and AI teams, or see{" "}
            <Link
              href="/alternatives"
              className="text-foreground decoration-foreground/30 hover:decoration-foreground underline underline-offset-4"
            >
              how we compare to other providers
            </Link>
            .
          </p>
        </div>

        {/* Two flagship cards with visuals */}
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid gap-4 sm:gap-5 lg:grid-cols-2"
        >
          <BigFeatureCard
            title="Real-time Dashboard"
            description="Monitor bandwidth usage, success rates, active sessions, and billing — all in one clean, live dashboard."
            reduced={prefersReducedMotion}
          >
            <DashboardMock />
          </BigFeatureCard>
          <BigFeatureCard
            title={`${uptime.toFixed(3)}% Uptime`}
            description="Proven uptime backed by redundant infrastructure across multiple data centers globally."
            reduced={prefersReducedMotion}
          >
            <UptimeMock uptime={uptime} status={uptimeStatus} />
          </BigFeatureCard>
        </motion.div>

        {/* Four supporting cards */}
        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="mt-4 grid grid-cols-1 gap-4 sm:mt-5 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4"
        >
          {smallFeatures.map((feature) => (
            <SmallFeatureCard
              key={feature.title}
              Visual={feature.Visual}
              title={feature.title}
              description={feature.description}
              reduced={prefersReducedMotion}
            />
          ))}
        </motion.div>
      </div>
    </section>
  );
}
